import { createIcon } from "./icons";

export interface FpsMeterOptions {
  /**
   * Coché/décoché depuis la modale de diagnostic du widget — bascule l'affichage de la grille de
   * pathfinding pour toute la zone actuellement visible (voir `MapAdapter.showPathfindingDebugGrid`/
   * `computeAreaDebugGrid`, et le bouton "Diagnostic" évoqué dans `createTokaMap.ts`). `FpsMeter`
   * ne connaît ni `MapAdapter` ni `ShapeStore` (même convention que `RoutePanel`/`SearchBox`, voir
   * CLAUDE.md) : c'est à l'appelant de recalculer et d'afficher/masquer la grille lui-même.
   */
  onToggleDebugGrid: (enabled: boolean) => void;
  /**
   * État initial de la case (voir `createTokaMap.ts`, qui le restaure depuis le localStorage partagé
   * par toute instance `@tokamap/core` — `FpsMeter` lui-même ne connaît pas cette persistance,
   * seulement l'état qu'on lui donne). Par défaut : `false`.
   */
  initialDebugGridEnabled?: boolean;
}

/**
 * Compteur FPS live superposé à l'écran (outil de diagnostic, voir main.ts) — porte aussi, depuis
 * son propre bouton diagnostic, les options de diagnostic ponctuelles de l'éditeur (pour l'instant :
 * la grille de pathfinding, voir `FpsMeterOptions.onToggleDebugGrid`) plutôt qu'un widget séparé :
 * décision produit explicite (voir historique de conception dans le CLAUDE.md) après avoir écarté
 * un bouton dans `Header`/`SettingsSidebar`, jugés trop visibles ou réservés aux réglages persistés.
 */
export class FpsMeter {
  private el: HTMLDivElement;
  private fpsText: HTMLSpanElement;
  private modalBackdrop: HTMLDivElement | null = null;
  private checkboxInput: HTMLInputElement | null = null;
  private debugGridEnabled: boolean;
  private frameCount = 0;
  private lastSampleAt = performance.now();
  private rafId: number;

  constructor(
    private container: HTMLElement,
    options: FpsMeterOptions,
  ) {
    this.debugGridEnabled = options.initialDebugGridEnabled ?? false;

    this.el = document.createElement("div");
    this.el.className = "fps-meter";

    this.fpsText = document.createElement("span");
    this.fpsText.textContent = "… fps";
    this.el.appendChild(this.fpsText);

    const diagnosticsButton = document.createElement("button");
    diagnosticsButton.type = "button";
    diagnosticsButton.className = "fps-meter__diagnostics-button";
    diagnosticsButton.setAttribute("aria-label", "Diagnostic");
    diagnosticsButton.appendChild(createIcon("debug"));
    diagnosticsButton.addEventListener("click", () => this.openModal(options));
    this.el.appendChild(diagnosticsButton);

    container.appendChild(this.el);
    this.rafId = requestAnimationFrame(this.tick);
  }

  /**
   * Une vraie modale (fond + panneau centré, fermeture explicite) plutôt qu'un popup ancré au
   * widget — un popup fermé sur "clic extérieur" s'est avéré se fermer aussi sur le clic qui coche
   * la case elle-même (le `click` de la case remonte jusqu'au listener document AVANT que le
   * navigateur ne déclenche son propre `change`, qui ne partait alors plus jamais) : ici, seul un
   * clic dont la CIBLE est le fond lui-même ferme la modale (voir `handleBackdropClick`), jamais un
   * clic sur son contenu — la case peut donc être cochée/décochée sans jamais fermer la modale.
   */
  private openModal(options: FpsMeterOptions): void {
    if (this.modalBackdrop) return;

    const backdrop = document.createElement("div");
    backdrop.className = "fps-meter__diagnostics-backdrop";
    backdrop.addEventListener("click", this.handleBackdropClick);

    const modal = document.createElement("div");
    modal.className = "fps-meter__diagnostics-modal";

    const header = document.createElement("div");
    header.className = "fps-meter__diagnostics-modal-header";
    const title = document.createElement("span");
    title.textContent = "Diagnostic";
    header.appendChild(title);
    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.className = "fps-meter__diagnostics-modal-close";
    closeButton.setAttribute("aria-label", "Fermer");
    closeButton.appendChild(createIcon("close"));
    closeButton.addEventListener("click", () => this.closeModal());
    header.appendChild(closeButton);
    modal.appendChild(header);

    const row = document.createElement("label");
    row.className = "fps-meter__diagnostics-modal-row";

    // Interrupteur stylé plutôt que la case native du navigateur (voir historique de conception :
    // "la modale est vraiment moche") — reste une vraie `<input type="checkbox">` en dessous (donc
    // toujours accessible/clavier), seulement son apparence est redessinée via
    // `.fps-meter__switch-track` (voir style.css).
    const switchWrap = document.createElement("span");
    switchWrap.className = "fps-meter__switch";
    const checkbox = document.createElement("input");
    checkbox.type = "checkbox";
    checkbox.className = "fps-meter__switch-input";
    checkbox.checked = this.debugGridEnabled;
    checkbox.addEventListener("change", () => {
      this.debugGridEnabled = checkbox.checked;
      options.onToggleDebugGrid(this.debugGridEnabled);
    });
    const track = document.createElement("span");
    track.className = "fps-meter__switch-track";
    switchWrap.appendChild(checkbox);
    switchWrap.appendChild(track);

    row.appendChild(switchWrap);
    row.appendChild(document.createTextNode("Grille de pathfinding"));
    modal.appendChild(row);
    this.checkboxInput = checkbox;

    // Même convention que les fields de la sidebar (`AreaThemeSection.tsx`, backoffice) : un texte
    // atténué sous le contrôle plutôt qu'un tooltip — explique ce que les couleurs représentent sans
    // avoir à deviner (voir `PATHFINDING_DEBUG_BLOCKED_LAYER_ID`/`PATHFINDING_DEBUG_CHEAP_LAYER_ID`,
    // `MapLibreAdapter.ts`).
    const hint = document.createElement("p");
    hint.className = "fps-meter__diagnostics-modal-hint";
    hint.textContent = "Rouge : cellules bloquées par un obstacle. Jaune → bleu : cellules à coût réduit près d'une forme attractive — plus la teinte tire vers le bleu, plus l'attractivité est forte.";
    modal.appendChild(hint);

    backdrop.appendChild(modal);
    // Sur `container` (voir `TokaMapConfig`/`createTokaMap.ts`) — jamais `document.body` — même
    // convention que le reste de `ui/*` : la modale ne couvre que la zone de la carte, pas toute la
    // page (une `TopBar` au-dessus, côté exemple, reste par ex. cliquable).
    this.container.appendChild(backdrop);
    this.modalBackdrop = backdrop;

    document.addEventListener("keydown", this.handleEscapeKey);
  }

  private closeModal(): void {
    document.removeEventListener("keydown", this.handleEscapeKey);
    this.modalBackdrop?.remove();
    this.modalBackdrop = null;
    this.checkboxInput = null;
  }

  /**
   * Reflète un changement d'état survenu AILLEURS (autre onglet/instance, voir `createTokaMap.ts`'s
   * écouteur `"storage"`) — met juste la case à jour si la modale est ouverte, ne redéclenche jamais
   * `FpsMeterOptions.onToggleDebugGrid` (l'appelant a déjà décidé/appliqué le changement lui-même ;
   * le redéclencher ici boucherait en rappelant `localStorage.setItem` avec la même valeur, sans
   * incidence réelle mais sans utilité non plus).
   */
  setDebugGridEnabled(enabled: boolean): void {
    this.debugGridEnabled = enabled;
    if (this.checkboxInput) this.checkboxInput.checked = enabled;
  }

  private handleBackdropClick = (event: MouseEvent): void => {
    if (event.target === this.modalBackdrop) this.closeModal();
  };

  private handleEscapeKey = (event: KeyboardEvent): void => {
    if (event.key === "Escape") this.closeModal();
  };

  private tick = (): void => {
    this.frameCount++;
    const now = performance.now();
    const elapsed = now - this.lastSampleAt;
    if (elapsed >= 500) {
      const fps = (this.frameCount * 1000) / elapsed;
      this.fpsText.textContent = `${fps.toFixed(0)} fps`;
      this.frameCount = 0;
      this.lastSampleAt = now;
    }
    this.rafId = requestAnimationFrame(this.tick);
  };

  destroy(): void {
    cancelAnimationFrame(this.rafId);
    this.closeModal();
    this.el.remove();
  }
}
